// Settings → In-App Bookings (Pro/Enterprise only).
//
// Owners control whether customers can book directly through ConfirmPH,
// which mode to use (request-only / automatic-hold / automatic-confirm-after-
// payment: last one is currently locked off until real payments are wired),
// deposit policy, advance booking window, business terms, and cancellation
// policy.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Zap, AlertTriangle, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import UpgradeCard from "../components/UpgradeCard";


export default function InAppBookingSettings() {
  const { user, company } = useAuth();
  const role = user?.account_type || user?.role;
  const isOwner = role === "business_owner" || role === "platform_admin";

  const [settings, setSettings] = useState(null);
  const [paymentReady, setPaymentReady] = useState(false);
  const [locked, setLocked] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/in-app-bookings/settings");
      setSettings(data.settings);
      setPaymentReady(data.payment_provider_ready);
      setLocked(data.automatic_confirm_after_payment_locked);
      // If the GET response signals the plan can't support in-app bookings,
      // surface the upgrade card immediately: no need to wait for a PUT to
      // fail first.
      if (data.plan_supports_in_app_bookings === false) {
        setPlanError({
          error_code: "plan_upgrade_required",
          required_plans: data.required_plans || ["pro", "enterprise"],
          current_plan: data.plan_type || "free",
          message:
            "In-app bookings are available on Pro and Enterprise. Your current plan only supports customer inquiries.",
        });
      } else {
        setPlanError(null);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (partial) => {
    setSaving(true);
    setPlanError(null);
    try {
      const { data } = await api.put("/in-app-bookings/settings", partial);
      setSettings(data.settings);
      toast.success("Saved");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error_code === "plan_upgrade_required") {
        setPlanError(detail);
      } else if (detail && typeof detail === "object" && detail.error_code === "payment_provider_not_ready") {
        toast.error(detail.message);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) return <div className="text-slate-500">Loading…</div>;

  if (planError) {
    return (
      <div className="max-w-2xl space-y-4" data-testid="iab-upgrade-card">
        <Link to="/app/settings" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> Back to Settings
        </Link>
        <UpgradeCard
          detail={planError} isOwner={isOwner}
          title="Accept direct online bookings"
          note={
            <>
              <strong>You can still receive customer inquiries on your current plan.</strong>{" "}
              Customers can use the &ldquo;Send rental inquiry&rdquo; form on your public profile &mdash;
              you&apos;ll see those requests in your dashboard and reply via Messenger, phone, or email.
            </>
          }
        />
      </div>
    );
  }

  const publicUrl = company
    ? `${window.location.origin}/business/${company.id}`
    : "";

  const copyPublicUrl = () => {
    navigator.clipboard?.writeText(publicUrl);
    toast.success("Public link copied");
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="iab-settings-page">
      <Link to="/app/settings" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back to Settings
      </Link>

      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-1">
          <Zap className="w-3.5 h-3.5" /> Pro / Enterprise
        </div>
        <h1 className="font-display text-3xl font-bold mt-1">In-app bookings</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Let customers find and book through ConfirmPH directly. Pick the booking mode that matches
          your trust level: request-only is the safest place to start.
        </p>
      </div>

      {/* Public business link */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="iab-public-url-card">
        <h2 className="font-display font-semibold">Your public business page</h2>
        <p className="text-xs text-slate-500 mt-0.5">Share this link with customers. It only accepts bookings if In-App is enabled below.</p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono">{publicUrl}</code>
          <button onClick={copyPublicUrl} className="text-xs px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-1" data-testid="iab-copy-url">
            <Copy className="w-3 h-3" /> Copy
          </button>
          <a href={publicUrl} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Open
          </a>
        </div>
      </div>

      {/* Enable toggle */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox" checked={settings.enable_in_app_bookings}
            onChange={(e) => save({ enable_in_app_bookings: e.target.checked })}
            disabled={saving} data-testid="iab-enable-checkbox"
            className="mt-1 w-5 h-5"
          />
          <div>
            <div className="font-semibold">Enable in-app bookings</div>
            <p className="text-xs text-slate-500 mt-0.5">
              When OFF, your public business page shows your fleet but no booking form. Customers can still find you.
            </p>
          </div>
        </label>
      </div>

      {/* Booking mode */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="iab-mode-card">
        <h2 className="font-display font-semibold">Booking mode</h2>
        <ModeOption
          value="request_only" selected={settings.booking_mode}
          title="Request-only (safest)"
          desc="Customer submits a request. Your staff reviews and approves before anything is confirmed."
          onSelect={() => save({ booking_mode: "request_only" })}
        />
        <ModeOption
          value="automatic_hold" selected={settings.booking_mode}
          title="Automatic hold"
          desc="Vehicle is temporarily held when a customer books. Staff confirms or the hold expires automatically."
          onSelect={() => save({ booking_mode: "automatic_hold" })}
        />
        <ModeOption
          value="automatic_confirm_after_payment" selected={settings.booking_mode}
          title="Automatic confirm after payment"
          desc="Customer pays deposit/full → booking auto-confirms. Requires a live payment provider."
          locked={locked}
          lockedReason="Requires a live payment provider. Use request-only or automatic hold for now."
          onSelect={() => { /* server refuses while locked */ }}
        />
      </div>

      {/* Deposit & policy block */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="iab-deposit-card">
        <h2 className="font-display font-semibold">Deposit</h2>
        <Toggle label="Require deposit"
          checked={settings.require_deposit}
          onChange={(v) => save({ require_deposit: v })} />
        {settings.require_deposit && (
          <div className="grid sm:grid-cols-3 gap-3">
            <label>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Deposit type</span>
              <select value={settings.deposit_type}
                       onChange={(e) => save({ deposit_type: e.target.value })}
                       data-testid="iab-deposit-type"
                       className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none">
                <option value="fixed_amount">Fixed amount</option>
                <option value="percentage">Percentage of total</option>
              </select>
            </label>
            {settings.deposit_type === "fixed_amount" ? (
              <NumberField label="Amount (PHP)" value={settings.deposit_amount}
                            onSave={(v) => save({ deposit_amount: v })} />
            ) : (
              <NumberField label="Percentage" value={settings.deposit_percentage}
                            onSave={(v) => save({ deposit_percentage: v })} />
            )}
            <Toggle label="Allow remainder in cash"
              checked={settings.allow_cash_remaining_balance}
              onChange={(v) => save({ allow_cash_remaining_balance: v })} />
          </div>
        )}
      </div>

      {/* Window / duration */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
        <h2 className="font-display font-semibold">Booking window</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <NumberField label="Min advance (hours)"
            value={settings.min_advance_booking_hours}
            onSave={(v) => save({ min_advance_booking_hours: v })} />
          <NumberField label="Max advance (days)"
            value={settings.max_advance_booking_days}
            onSave={(v) => save({ max_advance_booking_days: v })} />
          <NumberField label="Minimum rental (hours)"
            value={settings.minimum_rental_duration_hours}
            onSave={(v) => save({ minimum_rental_duration_hours: v })} />
        </div>
        <NumberField label="Auto-expire unpaid hold (minutes)"
          value={settings.auto_expire_unpaid_booking_minutes}
          onSave={(v) => save({ auto_expire_unpaid_booking_minutes: v })} />
      </div>

      {/* Pickup + delivery */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
        <h2 className="font-display font-semibold">Pickup &amp; delivery</h2>
        <TextField label="Default pickup location"
          value={settings.default_pickup_location}
          onSave={(v) => save({ default_pickup_location: v })}
          placeholder="e.g. Cebu City office, Mango Avenue" />
        <TextField label="Pickup / delivery options (free text)"
          value={settings.pickup_delivery_options}
          onSave={(v) => save({ pickup_delivery_options: v })}
          rows={3}
          placeholder="e.g. Free pickup at office, hotel delivery +₱200, airport delivery +₱500" />
      </div>

      {/* Terms */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
        <h2 className="font-display font-semibold">Business terms &amp; cancellation policy</h2>
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 inline-flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Customers must accept these before any in-app booking. Keep them clear and short.</span>
        </div>
        <TextField label="Business terms / customer rules"
          value={settings.business_terms}
          onSave={(v) => save({ business_terms: v })}
          rows={4}
          placeholder="e.g. Driver must be 21+. Pickup hours 8am–8pm. Fuel returned same level." />
        <TextField label="Cancellation policy"
          value={settings.cancellation_policy}
          onSave={(v) => save({ cancellation_policy: v })}
          rows={3}
          placeholder="e.g. Full refund 48h+ before pickup. 50% refund 24–48h before. No refund within 24h." />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <Toggle label="Require staff final approval (recommended)"
          checked={settings.require_staff_final_approval}
          onChange={(v) => save({ require_staff_final_approval: v })} />
        <p className="text-xs text-slate-500 mt-1">
          Even in automatic_hold mode, staff must still tap Approve. Disable only if you're confident
          your availability calendar is always up-to-date.
        </p>
      </div>
    </div>
  );
}

function ModeOption({ value, selected, title, desc, locked, lockedReason, onSelect }) {
  const checked = selected === value;
  const disabledStyle = locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-slate-50";
  return (
    <label className={`block rounded-lg border ${checked ? "border-emerald-500 bg-emerald-50" : "border-slate-200"} p-3 ${disabledStyle}`}
           data-testid={`iab-mode-${value}`}>
      <div className="flex items-start gap-3">
        <input type="radio" name="iab_mode" checked={checked} disabled={locked}
                onChange={() => onSelect()} className="mt-1" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{title}</span>
            {locked && <span className="text-[10px] px-2 py-0.5 rounded-full border border-rose-300 bg-rose-50 text-rose-700 uppercase tracking-wider">Locked</span>}
          </div>
          <p className="text-xs text-slate-600 mt-0.5">{desc}</p>
          {locked && <p className="text-xs text-rose-700 mt-1">{lockedReason}</p>}
        </div>
      </div>
    </label>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-sm">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function NumberField({ label, value, onSave }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => setV(value ?? ""), [value]);
  return (
    <label>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <input type="number" value={v}
              onChange={(e) => setV(e.target.value)}
              onBlur={() => { if (String(v) !== String(value)) onSave(Number(v) || 0); }}
              className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none font-mono" />
    </label>
  );
}

function TextField({ label, value, onSave, rows = 1, placeholder }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => setV(value ?? ""), [value]);
  const Tag = rows > 1 ? "textarea" : "input";
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <Tag
        value={v}
        rows={rows > 1 ? rows : undefined}
        placeholder={placeholder}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => { if (v !== value) onSave(v); }}
        className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none"
      />
    </label>
  );
}
